import { useEffect, useState } from "react";
import { Tags, Plus, Check, X as XIcon, Pencil } from "lucide-react";
import client, { extractError } from "@/api";

export default function AdminReferencePage({ mode }) {
  // mode: "categories" or "cities"
  const isCat = mode === "categories";
  return isCat ? <CategoriesAdmin /> : <CitiesAdmin />;
}

/* -------------------------------------------------- CATEGORIES ---- */
function CategoriesAdmin() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);
  const [newCat, setNewCat] = useState({ name: "", slug: "", description: "" });
  const [addSubFor, setAddSubFor] = useState(null);
  const [newSub, setNewSub] = useState({ name: "", slug: "" });
  const [editCat, setEditCat] = useState(null);
  const [editSub, setEditSub] = useState(null);

  const load = () => client.get("/admin/reference/categories").then((r) => setRows(r.data)).catch((err) => setError(extractError(err))).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const createCategory = async () => {
    if (!newCat.name || !newCat.slug) { setError("Name and slug are required"); return; }
    try { await client.post("/admin/reference/categories", newCat); setNewCat({ name: "", slug: "", description: "" }); setCreating(false); await load(); }
    catch (err) { setError(extractError(err)); }
  };
  const patchCategory = async (id, body) => {
    try { await client.patch(`/admin/reference/categories/${id}`, body); await load(); }
    catch (err) { setError(extractError(err)); }
  };
  const createSubcategory = async (catId) => {
    if (!newSub.name || !newSub.slug) { setError("Name and slug are required"); return; }
    try { await client.post("/admin/reference/subcategories", { category_id: catId, ...newSub }); setNewSub({ name: "", slug: "" }); setAddSubFor(null); await load(); }
    catch (err) { setError(extractError(err)); }
  };
  const patchSubcategory = async (id, body) => {
    try { await client.patch(`/admin/reference/subcategories/${id}`, body); await load(); }
    catch (err) { setError(extractError(err)); }
  };

  if (loading) return <div className="loading-block">Loading categories…</div>;
  return (
    <div className="admin-page" data-testid="admin-categories-page">
      <header className="page-header">
        <div><h2><Tags size={18} /> Categories & Subcategories</h2><p className="muted">Manage the master list of business categories. Deactivate to hide from new listings while preserving history.</p></div>
        <button type="button" className="primary-button" data-testid="new-category-btn" onClick={() => setCreating(!creating)}><Plus size={14} /> New category</button>
      </header>
      {error && <div className="form-error">{error}</div>}
      {creating && (
        <section className="detail-grid" data-testid="new-category-form">
          <article><label>Name</label><input value={newCat.name} onChange={(e) => setNewCat({ ...newCat, name: e.target.value })} data-testid="new-category-name" /></article>
          <article><label>Slug</label><input value={newCat.slug} onChange={(e) => setNewCat({ ...newCat, slug: e.target.value })} placeholder="e.g. professional-services" data-testid="new-category-slug" /></article>
          <article className="wide"><label>Description</label><input value={newCat.description} onChange={(e) => setNewCat({ ...newCat, description: e.target.value })} data-testid="new-category-desc" /></article>
          <article><button type="button" className="primary-button" onClick={createCategory} data-testid="save-new-category">Save</button></article>
        </section>
      )}
      <div className="tables-block">
        {rows.map((c) => (
          <article key={c.id} className="section-block" data-testid={`category-${c.id}`}>
            <div className="section-heading">
              <div>
                {editCat?.id === c.id ? (
                  <input value={editCat.name} onChange={(e) => setEditCat({ ...editCat, name: e.target.value })} data-testid={`edit-category-name-${c.id}`} />
                ) : (
                  <h3>{c.name}{!c.is_active && <span className="chip status-cancelled" data-testid={`inactive-${c.id}`}> INACTIVE</span>}</h3>
                )}
                <p className="muted small">Slug: {c.slug} · Order: {c.display_order} · {c.subcategories.length} subcategor{c.subcategories.length === 1 ? "y" : "ies"}</p>
              </div>
              <div className="chip-stack">
                {editCat?.id === c.id ? (
                  <>
                    <button type="button" className="primary-button" data-testid={`save-category-${c.id}`} onClick={async () => { await patchCategory(c.id, { name: editCat.name }); setEditCat(null); }}><Check size={13} /> Save</button>
                    <button type="button" className="ghost-button" onClick={() => setEditCat(null)}>Cancel</button>
                  </>
                ) : (
                  <>
                    <button type="button" className="ghost-button" data-testid={`edit-category-${c.id}`} onClick={() => setEditCat({ id: c.id, name: c.name })}><Pencil size={13} /> Edit</button>
                    <button type="button" className={c.is_active ? "danger-button" : "primary-button"} data-testid={`toggle-category-${c.id}`} onClick={() => patchCategory(c.id, { is_active: !c.is_active })}>
                      {c.is_active ? <><XIcon size={13} /> Deactivate</> : <><Check size={13} /> Activate</>}
                    </button>
                    <button type="button" className="ghost-button" data-testid={`add-sub-${c.id}`} onClick={() => setAddSubFor(addSubFor === c.id ? null : c.id)}><Plus size={13} /> Subcategory</button>
                  </>
                )}
              </div>
            </div>
            {addSubFor === c.id && (
              <div className="detail-grid" data-testid={`new-sub-form-${c.id}`}>
                <article><label>Name</label><input value={newSub.name} onChange={(e) => setNewSub({ ...newSub, name: e.target.value })} data-testid={`new-sub-name-${c.id}`} /></article>
                <article><label>Slug</label><input value={newSub.slug} onChange={(e) => setNewSub({ ...newSub, slug: e.target.value })} data-testid={`new-sub-slug-${c.id}`} /></article>
                <article><button type="button" className="primary-button" onClick={() => createSubcategory(c.id)} data-testid={`save-new-sub-${c.id}`}>Save subcategory</button></article>
              </div>
            )}
            <ul className="finance-list">
              {c.subcategories.length === 0 && <li className="muted small">No subcategories yet.</li>}
              {c.subcategories.map((s) => (
                <li key={s.id} data-testid={`subcategory-${s.id}`}>
                  {editSub?.id === s.id ? (
                    <>
                      <input value={editSub.name} onChange={(e) => setEditSub({ ...editSub, name: e.target.value })} data-testid={`edit-sub-name-${s.id}`} />
                      <div className="chip-stack">
                        <button type="button" className="primary-button" onClick={async () => { await patchSubcategory(s.id, { name: editSub.name }); setEditSub(null); }} data-testid={`save-sub-${s.id}`}><Check size={13} /> Save</button>
                        <button type="button" className="ghost-button" onClick={() => setEditSub(null)}>Cancel</button>
                      </div>
                    </>
                  ) : (
                    <>
                      <span>{s.name} {!s.is_active && <span className="chip status-cancelled" data-testid={`inactive-sub-${s.id}`}> INACTIVE</span>}</span>
                      <div className="chip-stack">
                        <button type="button" className="ghost-button" onClick={() => setEditSub({ id: s.id, name: s.name })} data-testid={`edit-sub-${s.id}`}><Pencil size={12} /> Edit</button>
                        <button type="button" className={s.is_active ? "danger-button" : "primary-button"} data-testid={`toggle-sub-${s.id}`} onClick={() => patchSubcategory(s.id, { is_active: !s.is_active })}>
                          {s.is_active ? <><XIcon size={12} /> Deactivate</> : <><Check size={12} /> Activate</>}
                        </button>
                      </div>
                    </>
                  )}
                </li>
              ))}
            </ul>
          </article>
        ))}
      </div>
    </div>
  );
}

/* ---------------------------------------------------- CITIES ------ */
function CitiesAdmin() {
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [stateId, setStateId] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);
  const [newCity, setNewCity] = useState({ name: "", state_id: "" });
  const [editCity, setEditCity] = useState(null);

  useEffect(() => { client.get("/reference/states").then((r) => setStates(r.data)); }, []);

  const load = () => {
    setLoading(true);
    const q = stateId ? `?state_id=${stateId}` : "";
    return client.get(`/admin/reference/cities${q}`).then((r) => setCities(r.data)).catch((err) => setError(extractError(err))).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [stateId]);

  const createCity = async () => {
    if (!newCity.name || !newCity.state_id) { setError("Name and state are required"); return; }
    try { await client.post("/admin/reference/cities", newCity); setNewCity({ name: "", state_id: "" }); setCreating(false); await load(); }
    catch (err) { setError(extractError(err)); }
  };
  const patchCity = async (id, body) => {
    try { await client.patch(`/admin/reference/cities/${id}`, body); await load(); }
    catch (err) { setError(extractError(err)); }
  };

  return (
    <div className="admin-page" data-testid="admin-cities-page">
      <header className="page-header">
        <div><h2>Cities</h2><p className="muted">Manage the city master. Deactivating a city hides it from new records but preserves existing references.</p></div>
        <button type="button" className="primary-button" data-testid="new-city-btn" onClick={() => setCreating(!creating)}><Plus size={14} /> New city</button>
      </header>
      {error && <div className="form-error">{error}</div>}
      {creating && (
        <section className="detail-grid" data-testid="new-city-form">
          <article><label>Name</label><input value={newCity.name} onChange={(e) => setNewCity({ ...newCity, name: e.target.value })} data-testid="new-city-name" /></article>
          <article><label>State</label>
            <select value={newCity.state_id} onChange={(e) => setNewCity({ ...newCity, state_id: e.target.value })} data-testid="new-city-state">
              <option value="">Select state…</option>
              {states.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </article>
          <article><button type="button" className="primary-button" onClick={createCity} data-testid="save-new-city">Save</button></article>
        </section>
      )}
      <div className="detail-grid" style={{ marginBottom: 16 }}>
        <article><label>Filter by state</label>
          <select value={stateId} onChange={(e) => setStateId(e.target.value)} data-testid="city-state-filter">
            <option value="">All states</option>
            {states.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </article>
      </div>
      {loading && <div className="loading-block">Loading cities…</div>}
      {!loading && (
        <table className="admin-table" data-testid="city-table">
          <thead><tr><th>Name</th><th>State</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {cities.length === 0 && <tr><td colSpan="4" className="muted small">No cities.</td></tr>}
            {cities.map((c) => {
              const state = states.find((s) => s.id === c.state_id);
              return (
                <tr key={c.id} data-testid={`city-${c.id}`}>
                  <td>
                    {editCity?.id === c.id
                      ? <input value={editCity.name} onChange={(e) => setEditCity({ ...editCity, name: e.target.value })} data-testid={`edit-city-name-${c.id}`} />
                      : c.name}
                  </td>
                  <td>{state?.name || c.state_id}</td>
                  <td>{c.is_active
                    ? <span className="chip status-verified" data-testid={`city-active-${c.id}`}>Active</span>
                    : <span className="chip status-cancelled" data-testid={`city-inactive-${c.id}`}>Inactive</span>}</td>
                  <td>
                    <div className="chip-stack">
                      {editCity?.id === c.id ? (
                        <>
                          <button type="button" className="primary-button" data-testid={`save-city-${c.id}`} onClick={async () => { await patchCity(c.id, { name: editCity.name }); setEditCity(null); }}><Check size={12} /> Save</button>
                          <button type="button" className="ghost-button" onClick={() => setEditCity(null)}>Cancel</button>
                        </>
                      ) : (
                        <>
                          <button type="button" className="ghost-button" data-testid={`edit-city-${c.id}`} onClick={() => setEditCity({ id: c.id, name: c.name })}><Pencil size={12} /> Edit</button>
                          <button type="button" className={c.is_active ? "danger-button" : "primary-button"} data-testid={`toggle-city-${c.id}`} onClick={() => patchCity(c.id, { is_active: !c.is_active })}>
                            {c.is_active ? <><XIcon size={12} /> Deactivate</> : <><Check size={12} /> Activate</>}
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
